"use client";

import { Affix, Alert, Button, Group, Stack, Text } from "@mantine/core";
import { usePathname } from "next/navigation";
import { useState, useSyncExternalStore } from "react";
import {
  copyEditablePageText,
  getServerSessionExpirationSnapshot,
  getSessionExpirationSnapshot,
  resetSessionExpiration,
  subscribeToSessionExpiration,
} from "@/lib/auth/session-expired";

/**
 * Password-setting routes, where the banner never appears at all.
 *
 * `/welcome` signs the visitor out on mount before it can set a password, which trips the expiry
 * signal by design. A new patient's very first screen was telling them something had broken, and
 * there is nothing they could do about it — they have no session yet and no unsaved work.
 */
const PASSWORD_ROUTES = ["/welcome", "/reset-password"];

/**
 * Sign-in routes, where the banner appears but can be dismissed.
 *
 * "Your session has expired" is a reasonable thing to land on a login page — it explains why you
 * are there. It should not be permanent furniture, so these get a close button.
 */
const DISMISSABLE_ROUTES = ["/login", "/logout", "/admin-login"];

function matchesRoute(pathname: string | null, routes: string[]): boolean {
  return routes.some((route) => pathname === route || pathname?.startsWith(`${route}/`));
}

const copyButtonLabels = {
  idle: "Copy editable text",
  copied: "Editable text copied",
  empty: "No editable text found",
  failed: "Copy failed — select text manually",
} as const;

type CopyStatus = keyof typeof copyButtonLabels;

/**
 * Keeps expired sessions on the current page so unsaved work can be copied before sign-in.
 */
export function SessionExpiredBanner() {
  const recoverySnapshot = useSyncExternalStore(
    subscribeToSessionExpiration,
    getSessionExpirationSnapshot,
    getServerSessionExpirationSnapshot,
  );
  const pathname = usePathname();
  const [copyStatus, setCopyStatus] = useState<CopyStatus>("idle");
  const [isDismissed, setIsDismissed] = useState(false);

  const isPasswordRoute = matchesRoute(pathname, PASSWORD_ROUTES);
  const isDismissable = matchesRoute(pathname, DISMISSABLE_ROUTES);

  if (isPasswordRoute || !recoverySnapshot.recoveryRequired) {
    return null;
  }

  // Dismissal is component state on purpose: it silences this view of this page, not the signal.
  // A fresh expiry, or any surface where losing work is possible, brings the banner back.
  if (isDismissable && isDismissed) {
    return null;
  }

  const handleCopy = async () => {
    try {
      const copiedFieldCount = await copyEditablePageText();
      setCopyStatus(copiedFieldCount > 0 ? "copied" : "empty");
    } catch {
      setCopyStatus("failed");
    }
  };

  const isRestored = recoverySnapshot.phase === "restored";
  const title = isRestored ? "Your session has been restored" : "Your session has expired";

  return (
    <Affix position={{ top: 0, left: 0, right: 0 }} zIndex={1000}>
      <Alert
        color={isRestored ? "green" : "yellow"}
        radius={0}
        role="alert"
        title={title}
        withCloseButton={isDismissable}
        closeButtonLabel="Dismiss"
        onClose={() => setIsDismissed(true)}
      >
        <Stack gap="xs">
          <Text size="sm">
            {isRestored
              ? "This tab has verified the new login and refreshed its data. You can continue without reloading."
              : "This page will stay open so you can copy unsaved work. Saving will not work until you sign in again."}
          </Text>
          <Group gap="xs">
            <Button color="yellow" variant="outline" onClick={handleCopy}>
              {copyButtonLabels[copyStatus]}
            </Button>
            {isRestored ? (
              <Button onClick={resetSessionExpiration}>Continue working</Button>
            ) : (
              <Button component="a" href="/logout">
                Sign in again
              </Button>
            )}
          </Group>
        </Stack>
      </Alert>
    </Affix>
  );
}
